import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Print as PrintIcon,
  Description as DocIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as FeatureIcon,
  NotificationsActive as AlertIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { EasyCheckButton } from '../../common/Controls';
import { companyPreferencesService } from '../../../services/settings/companyPreferencesService';
import { documentTypeService } from '../../../services/documentTypes/documentTypeService';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import { useNavigate } from 'react-router-dom';
import { ReceiptLong as VoucherIcon, Sync as ZohoIcon } from '@mui/icons-material';
import { canAccess } from '../../../utils/planPermissions';
import { zohoService } from '../../../services/zoho/zohoService';

const SettingsContainer = styled(Box)(({ theme }) => ({
  maxWidth: '800px',
  margin: '0 auto',
  padding: theme.spacing(3),
  height: 'calc(100vh - 64px)',
  overflowY: 'auto',
}));

const SettingsCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const emptyDocType = { typeName: '' };

const PreferencesPage = () => {
  const { user } = useEasyCheck();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const isAdmin = user?.role === 'Admin';

  // Document Types state
  const [docTypeDialogOpen, setDocTypeDialogOpen] = useState(false);
  const [editingDocType, setEditingDocType] = useState(null);
  const [docTypeForm, setDocTypeForm] = useState(emptyDocType);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const { data: preferences, isLoading } = useQuery({
    queryKey: ['companyPreferences'],
    queryFn: async () => {
      const result = await companyPreferencesService.getPreferences();
      return result.data;
    },
  });

  // Zoho card: plans with zohoIntegration, or (any plan) admins of a company already on the webhook
  const zohoPlanAllowed = canAccess(user?.planId, 'zohoIntegration');
  const { data: zohoWebhookKeys } = useQuery({
    queryKey: ['zohoWebhookKeys'],
    queryFn: () => zohoService.getWebhookKeys(),
    enabled: isAdmin && !zohoPlanAllowed,
    retry: false,
  });
  const showZohoCard = zohoPlanAllowed || (zohoWebhookKeys?.keys?.length || 0) > 0;

  const [localPrefs, setLocalPrefs] = useState(null);

  useEffect(() => {
    if (preferences) {
      setLocalPrefs(preferences);
    }
  }, [preferences]);

  const saveMutation = useMutation({
    mutationFn: (prefs) => companyPreferencesService.savePreferences(prefs),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['companyPreferences'] });
        showSuccess(result.message || 'Preferences saved successfully');
      } else {
        showError(result.message || 'Failed to save preferences');
      }
    },
    onError: () => {
      showError('Failed to save preferences');
    },
  });

  // Document Types queries
  const { data: documentTypesData, isLoading: isLoadingDocTypes } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: async () => {
      const result = await documentTypeService.getAll();
      return result.data || [];
    },
  });

  const createDocTypeMutation = useMutation({
    mutationFn: (data) => documentTypeService.create(data),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['documentTypes'] });
        showSuccess('Document type created successfully');
        handleCloseDocTypeDialog();
      } else {
        showError(result.message || 'Failed to create document type');
      }
    },
    onError: () => showError('Failed to create document type'),
  });

  const updateDocTypeMutation = useMutation({
    mutationFn: ({ id, data }) => documentTypeService.update(id, data),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['documentTypes'] });
        showSuccess('Document type updated successfully');
        handleCloseDocTypeDialog();
      } else {
        showError(result.message || 'Failed to update document type');
      }
    },
    onError: () => showError('Failed to update document type'),
  });

  const deleteDocTypeMutation = useMutation({
    mutationFn: (id) => documentTypeService.delete(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['documentTypes'] });
        showSuccess('Document type deleted successfully');
      } else {
        showError(result.message || 'Failed to delete document type');
      }
      setDeleteConfirmId(null);
    },
    onError: () => {
      showError('Failed to delete document type');
      setDeleteConfirmId(null);
    },
  });

  const handleOpenDocTypeDialog = (docType = null) => {
    if (docType) {
      setEditingDocType(docType);
      setDocTypeForm({
        typeName: docType.TypeName || docType.typeName || '',
      });
    } else {
      setEditingDocType(null);
      setDocTypeForm(emptyDocType);
    }
    setDocTypeDialogOpen(true);
  };

  const handleCloseDocTypeDialog = () => {
    setDocTypeDialogOpen(false);
    setEditingDocType(null);
    setDocTypeForm(emptyDocType);
  };

  const handleSaveDocType = () => {
    if (!docTypeForm.typeName.trim()) return;
    const name = docTypeForm.typeName.trim();
    const typeCode = name.substring(0, 10).toUpperCase().replace(/\s+/g, '_');
    const payload = {
      typeName: name,
      typeCode,
      description: '',
    };
    if (editingDocType) {
      const id = editingDocType.Id || editingDocType.id;
      updateDocTypeMutation.mutate({ id, data: payload });
    } else {
      createDocTypeMutation.mutate(payload);
    }
  };

  const handleToggle = (key) => () => {
    setLocalPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleNumberChange = (key) => (e) => {
    const val = parseInt(e.target.value, 10);
    setLocalPrefs((prev) => ({
      ...prev,
      [key]: isNaN(val) ? 0 : Math.max(0, Math.min(365, val)),
    }));
  };

  const handleSave = () => {
    saveMutation.mutate(localPrefs);
  };

  const hasChanges = localPrefs && preferences &&
    JSON.stringify(localPrefs) !== JSON.stringify(preferences);

  if (isLoading || !localPrefs) {
    return (
      <SettingsContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      </SettingsContainer>
    );
  }

  const printOptions = [
    {
      key: 'includeAcPayee',
      label: 'A/C Payee',
      description: 'Print "A/C Payee Only" crossing on the cheque',
    },
    {
      key: 'includeBeneficiary',
      label: 'Beneficiary Name',
      description: 'Print the beneficiary name on the cheque',
    },
    {
      key: 'includeDate',
      label: 'Date',
      description: 'Print the cheque date',
    },
    {
      key: 'includeAmount',
      label: 'Amount',
      description: 'Print the cheque amount (both numerical and in words)',
    },
    {
      key: 'includeAmountPrefix',
      label: 'Amount Prefix **',
      description: 'Print ** before the amount for security',
    },
    {
      key: 'includeAmountSuffix',
      label: 'Amount Suffix **',
      description: 'Print ** after the amount for security',
    },
    {
      key: 'includeBeneficiary2',
      label: 'Beneficiary Line 2',
      description: 'Print the second line of beneficiary name',
    },
    {
      key: 'includeRemarks',
      label: 'Remarks',
      description: 'Print remarks/memo on the cheque',
    },
    {
      key: 'includeNonNegotiable',
      label: 'Non Negotiable',
      description: 'Print "Non Negotiable" marking on the cheque',
    },
    {
      key: 'chequeSizedPdf',
      label: 'Cheque Sized PDF',
      description: 'Generate PDF matching exact cheque dimensions (for printers with non-centered trays)',
    },
    {
      key: 'excludeCurrencyWords',
      label: 'Exclude Currency Name in Words',
      description: 'Print the amount in words without the currency unit (e.g. "ONE THOUSAND FIVE HUNDRED ONLY")',
    },
  ];

  return (
    <SettingsContainer>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
        Company Preferences
      </Typography>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Only administrators can change company preferences.
        </Alert>
      )}

      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <PrintIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Cheque Print Defaults
            </Typography>
          </SectionHeader>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set company-wide default print options. These defaults will be pre-selected when any user opens the print configuration dialog. Users can still override these settings per print.
          </Typography>

          <Grid container spacing={2}>
            {printOptions.map((option) => (
              <Grid item xs={12} sm={6} key={option.key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPrefs[option.key]}
                      onChange={handleToggle(option.key)}
                      color="primary"
                      disabled={!isAdmin}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    display: 'flex',
                    mb: 0,
                    ml: 0,
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {isAdmin && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <EasyCheckButton
                buttonType="form"
                onClick={handleSave}
                loading={saveMutation.isPending}
                disabled={!hasChanges}
              >
                Save Preferences
              </EasyCheckButton>
            </Box>
          )}
        </CardContent>
      </SettingsCard>

      {/* Feature Modules Section */}
      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <FeatureIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Feature Modules
            </Typography>
          </SectionHeader>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enable or disable optional feature modules for your company. Disabled features are completely hidden from all users.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localPrefs.enableChequeCollection || false}
                    onChange={handleToggle('enableChequeCollection')}
                    color="primary"
                    disabled={!isAdmin}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Cheque Collection
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Track physical handover of printed cheques to suppliers (walk-in and courier delivery)
                    </Typography>
                  </Box>
                }
                sx={{
                  display: 'flex',
                  mb: 0,
                  ml: 0,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localPrefs.enableSignedStatus || false}
                    onChange={handleToggle('enableSignedStatus')}
                    color="primary"
                    disabled={!isAdmin}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Signed Status Tracking
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Track which printed cheques have been signed by an authorized signatory before handover
                    </Typography>
                  </Box>
                }
                sx={{
                  display: 'flex',
                  mb: 0,
                  ml: 0,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localPrefs.enableUserPermissions || false}
                    onChange={handleToggle('enableUserPermissions')}
                    color="primary"
                    disabled={!isAdmin}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Per-User Module Permissions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Let admins choose which modules each User-level user can access (User Management → Edit Role & Access)
                    </Typography>
                  </Box>
                }
                sx={{
                  display: 'flex',
                  mb: 0,
                  ml: 0,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localPrefs.enableBankBackup || false}
                    onChange={handleToggle('enableBankBackup')}
                    color="primary"
                    disabled={!isAdmin}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Bank Configuration Backup
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Export, import and restore bank accounts and cheque templates, with automatic template version history
                    </Typography>
                  </Box>
                }
                sx={{
                  display: 'flex',
                  mb: 0,
                  ml: 0,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localPrefs.enableVoucherPrinting || false}
                    onChange={handleToggle('enableVoucherPrinting')}
                    color="primary"
                    disabled={!isAdmin}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Payment Voucher Printing
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Print a payment voucher for any cheque from the Print Cheques page; design it in the Voucher Design card below
                    </Typography>
                  </Box>
                }
                sx={{
                  display: 'flex',
                  mb: 0,
                  ml: 0,
                }}
              />
            </Grid>
          </Grid>

          {isAdmin && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <EasyCheckButton
                buttonType="form"
                onClick={handleSave}
                loading={saveMutation.isPending}
                disabled={!hasChanges}
              >
                Save Preferences
              </EasyCheckButton>
            </Box>
          )}
        </CardContent>
      </SettingsCard>

      {/* Voucher Design — summary card; the full-screen designer lives at /preferences/voucher-design */}
      {localPrefs.enableVoucherPrinting && (
        <SettingsCard>
          <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <VoucherIcon sx={{ color: '#2563eb', fontSize: 28 }} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Voucher Design
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Header, logo, labels, numbering and signature lines for the printed payment voucher, with a live A4 preview.
              </Typography>
              {!preferences?.enableVoucherPrinting && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                  Save Preferences first to enable the designer.
                </Typography>
              )}
            </Box>
            <EasyCheckButton
              buttonType="action"
              onClick={() => navigate('/preferences/voucher-design')}
              disabled={!preferences?.enableVoucherPrinting}
            >
              Open designer
            </EasyCheckButton>
          </CardContent>
        </SettingsCard>
      )}

      {/* Zoho Books — plan-gated (Business Plus / Premium); setup lives at /preferences/zoho */}
      {showZohoCard && (
        <SettingsCard>
          <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ZohoIcon sx={{ color: '#2563eb', fontSize: 28 }} />
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Zoho Books Integration
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bring Zoho Books cheque payments into EasyCheck by Webhook (default) or by API Pull — connect your own
                Zoho Books and import payments from the Print Cheques page.
              </Typography>
            </Box>
            <EasyCheckButton buttonType="action" onClick={() => navigate('/preferences/zoho')}>
              {isAdmin ? 'Set up' : 'View'}
            </EasyCheckButton>
          </CardContent>
        </SettingsCard>
      )}

      {/* PDC Alerts Section */}
      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <AlertIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              PDC Alerts
            </Typography>
          </SectionHeader>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get notified before post-dated cheques are due. Configure up to 3 alert thresholds (days before cheque date).
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localPrefs.enablePdcAlerts || false}
                    onChange={handleToggle('enablePdcAlerts')}
                    color="primary"
                    disabled={!isAdmin}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      Enable PDC Alerts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Show alert banners on the dashboard for upcoming post-dated cheques
                    </Typography>
                  </Box>
                }
                sx={{ display: 'flex', mb: 0, ml: 0 }}
              />
            </Grid>

            {localPrefs.enablePdcAlerts && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Alert 1 — Days Before"
                    type="number"
                    value={localPrefs.pdcAlertDays1 || ''}
                    onChange={handleNumberChange('pdcAlertDays1')}
                    fullWidth
                    size="small"
                    disabled={!isAdmin}
                    inputProps={{ min: 1, max: 365 }}
                    helperText="e.g. 10 days before due date"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Alert 2 — Days Before (optional)"
                    type="number"
                    value={localPrefs.pdcAlertDays2 || ''}
                    onChange={handleNumberChange('pdcAlertDays2')}
                    fullWidth
                    size="small"
                    disabled={!isAdmin}
                    inputProps={{ min: 0, max: 365 }}
                    helperText="Leave empty to skip"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Alert 3 — Days Before (optional)"
                    type="number"
                    value={localPrefs.pdcAlertDays3 || ''}
                    onChange={handleNumberChange('pdcAlertDays3')}
                    fullWidth
                    size="small"
                    disabled={!isAdmin}
                    inputProps={{ min: 0, max: 365 }}
                    helperText="Leave empty to skip"
                  />
                </Grid>
              </>
            )}
          </Grid>

          {isAdmin && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <EasyCheckButton
                buttonType="form"
                onClick={handleSave}
                loading={saveMutation.isPending}
                disabled={!hasChanges}
              >
                Save Preferences
              </EasyCheckButton>
            </Box>
          )}
        </CardContent>
      </SettingsCard>

      {/* Document Types Section */}
      <SettingsCard>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader>
            <DocIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
              Document Types
            </Typography>
            {isAdmin && (
              <EasyCheckButton
                buttonType="action"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDocTypeDialog()}
                size="small"
              >
                Add Type
              </EasyCheckButton>
            )}
          </SectionHeader>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define the document types that can be attached to cheques (e.g. Invoice, Purchase Order, Receipt).
          </Typography>

          {isLoadingDocTypes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !documentTypesData?.length ? (
            <Alert severity="info">
              No document types defined yet.{isAdmin ? ' Click "Add Type" to create one.' : ' Ask an administrator to set them up.'}
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Document Type</TableCell>
                    {isAdmin && <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentTypesData.map((dt) => {
                    const id = dt.Id || dt.id;
                    const name = dt.TypeName || dt.typeName;
                    return (
                      <TableRow key={id}>
                        <TableCell>{name}</TableCell>
                        {isAdmin && (
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => handleOpenDocTypeDialog(dt)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </SettingsCard>

      {/* Document Type Add/Edit Dialog */}
      <Dialog open={docTypeDialogOpen} onClose={handleCloseDocTypeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDocType ? 'Edit Document Type' : 'Add Document Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Document Type Name"
              value={docTypeForm.typeName}
              onChange={(e) => setDocTypeForm((prev) => ({ ...prev, typeName: e.target.value }))}
              placeholder="e.g. Invoice, Purchase Order"
              required
              inputProps={{ maxLength: 100 }}
              fullWidth
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={handleCloseDocTypeDialog}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            onClick={handleSaveDocType}
            loading={createDocTypeMutation.isPending || updateDocTypeMutation.isPending}
            disabled={!docTypeForm.typeName.trim()}
          >
            {editingDocType ? 'Update' : 'Create'}
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} maxWidth="xs">
        <DialogTitle>Delete Document Type</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document type? This cannot be undone if the type is not in use.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            color="error"
            onClick={() => deleteDocTypeMutation.mutate(deleteConfirmId)}
            loading={deleteDocTypeMutation.isPending}
          >
            Delete
          </EasyCheckButton>
        </DialogActions>
      </Dialog>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </SettingsContainer>
  );
};

export default PreferencesPage;
